import React from 'react';

import { ButtonSize } from '@components/Button/variants';
import EmailLeadCapture from '@components/EmailLeadCapture';
import { useAppContext } from '@contexts/AppContext';
import { static_privacy_path } from '@routes';
import { highlight_path } from '@routes';
import { Newsletter } from '@utils/backend-api/newsletters';
import { FormLocation } from '@utils/enums';
import { parameterize, transformToSnakeCase } from '@utils/formatting';
import { handleSubscriberRedirect } from '@utils/newsletterHelper';

import NewsletterSignupButton from './NewsletterSignupButton';
import styles from './styles.module.scss';

interface NewsletterSignupProps {
  buttonSize?: ButtonSize;
  currentUserAccessLevel: number;
  isLoggedIn: boolean;
  gdpr: boolean;
  isSubscribed: boolean;
  newsletter: Newsletter;
  headerChild?: boolean;
}

const NewsletterSignUp = ({
  buttonSize = 'big',
  currentUserAccessLevel,
  isLoggedIn,
  gdpr,
  isSubscribed,
  newsletter,
}: NewsletterSignupProps) => {
  const { initialCurrentUser } = useAppContext();
  const canSubscribe = currentUserAccessLevel >= newsletter.accessLevel;
  const canSubscribeToNewsletter = canSubscribe || !newsletter.isSubscriberOnly || isLoggedIn;

  return (
    <div className={styles.newsletterSignUp}>
      {isLoggedIn ? (
        <NewsletterSignupButton
          buttonSize={buttonSize}
          canSubscribe={canSubscribe}
          currentUserAccessLevel={currentUserAccessLevel}
          isSubscribed={isSubscribed}
          newsletter={newsletter}
        />
      ) : (
        <>
          <EmailLeadCapture
            buttonText="Get started"
            formClassName="mb-8 sm:col-span-2"
            formLocation={FormLocation.NewsletterSignup}
            gdpr={gdpr}
            gdprClassName="mb-16"
            inputClassName="w-full"
            leadType={`${transformToSnakeCase(newsletter.name)}_newsletter_lead_type`}
            newsletterIds={[Number(newsletter.id)]}
            redirectToOnSuccess={handleSubscriberRedirect({
              afterSignUpUrl: newsletter.afterSignUpUrl,
              canSubscribeToNewsletter: canSubscribeToNewsletter,
              isCurrentlySubscriber: initialCurrentUser?.is_subscriber,
              isPro: newsletter.isProOnly,
              isProSubscriber: initialCurrentUser?.is_pro_subscriber,
              offerCode: newsletter.offerCode,
              redirectTo: !canSubscribeToNewsletter
                ? highlight_path(parameterize(newsletter.name))
                : undefined,
            })}
            signInOptionClass="text-center sm:text-left"
            toastMessageOnSuccess={
              canSubscribeToNewsletter ? `Thank you for signing up for ${newsletter.name}.` : null
            }
          />
          <div className={styles.privacy}>
            By providing your email, you agree to The Information&rsquo;s{' '}
            <a href={static_privacy_path()}>Privacy Policy</a>
          </div>
        </>
      )}
    </div>
  );
};

export default NewsletterSignUp;
